import React, { useEffect, useState, useCallback, useRef } from "react";
import styled from "styled-components/macro";
import Auth from "../../Auth.js";
import firebase from "../../Firebase.js";
import { applyTheme } from "../../lib/Theme";
import { Card } from "../Card";
import {
  Logo,
  ContentWrapper,
  BackArrow,
  BackButton,
  PageWrapper,
  ErrorMessage,
  FieldGroup,
  FieldLabel,
  EmailValue,
} from "../PageShell";
import { SudoButton } from "../../sudokit/components/SudoButton";
import { Input } from "../../sudokit/components/Input";
import MenuSelect from "../../sudokit/components/SudoSelect/MenuSelect";
import {
  getCanSubmit,
  getCtaLabel,
  getNameDirty,
  getTaxTypeLabel,
  shouldShowTaxIdInputs,
} from "./taxIdLogic";

const TAX_ID_TYPES = [
  { value: "us_ein", label: "US EIN" },
  { value: "eu_vat", label: "EU VAT" },
  { value: "gb_vat", label: "GB VAT" },
  { value: "ca_gst_hst", label: "Canada GST/HST" },
  { value: "ca_bn", label: "Canada BN" },
  { value: "au_abn", label: "Australia ABN" },
  { value: "nz_gst", label: "New Zealand GST" },
  { value: "in_gst", label: "India GST" },
  { value: "jp_cn", label: "Japan CN" },
  { value: "jp_rn", label: "Japan RN" },
  { value: "kr_brn", label: "South Korea BRN" },
  { value: "sg_gst", label: "Singapore GST" },
  { value: "br_cnpj", label: "Brazil CNPJ" },
  { value: "br_cpf", label: "Brazil CPF" },
  { value: "mx_rfc", label: "Mexico RFC" },
  { value: "za_vat", label: "South Africa VAT" },
  { value: "ch_vat", label: "Switzerland VAT" },
  { value: "no_vat", label: "Norway VAT" },
  { value: "ae_trn", label: "UAE TRN" },
  { value: "il_vat", label: "Israel VAT" },
  { value: "tw_vat", label: "Taiwan VAT" },
  { value: "th_vat", label: "Thailand VAT" },
  { value: "my_sst", label: "Malaysia SST" },
  { value: "hk_br", label: "Hong Kong BR" },
];

export default function TaxId({ user, openLogin }) {
  const [loading, setLoading] = useState(true);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [taxIds, setTaxIds] = useState([]);
  const [taxType, setTaxType] = useState("eu_vat");
  const [taxValue, setTaxValue] = useState("");
  const [nameInput, setNameInput] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);
  const taxTypeSelectRef = useRef(null);

  useEffect(() => {
    applyTheme("sorbet");
  }, []);

  const fetchInfo = useCallback(async () => {
    setLoading(true);
    try {
      const result = await Auth.fetchWithIdToken("/api/tax-id/info", {});
      if (result.success) {
        setHasSubscription(true);
        setCustomer(result.customer);
        setNameInput(result.customer?.name || "");
        setTaxIds(result.taxIds || []);
      } else {
        setHasSubscription(false);
      }
    } catch (e) {
      setHasSubscription(false);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (user) {
      fetchInfo();
    } else {
      setLoading(false);
    }
  }, [user, fetchInfo]);

  const handleSwitchAccount = async () => {
    try {
      await firebase.auth().signOut();
    } catch (e) {
      // Continue even if signOut fails — we still want to show the login modal
    }
    if (openLogin) {
      openLogin();
    }
  };

  const trimmedName = nameInput.trim();
  const trimmedTaxValue = taxValue.trim();
  const showTaxIdInputs = shouldShowTaxIdInputs(taxIds);
  const nameDirty = getNameDirty(nameInput, customer);
  // The tax-ID inputs are hidden when one already exists, so any stale
  // value in state must be ignored when deciding whether to submit one.
  const taxIdProvided = showTaxIdInputs && trimmedTaxValue !== "";
  const canSubmit = getCanSubmit(nameInput, taxValue, customer, taxIds);
  const ctaLabel = getCtaLabel(submitting);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setStatusMessage(null);

    const tasks = [];
    if (nameDirty) {
      tasks.push({
        kind: "name",
        promise: Auth.fetchWithIdToken("/api/tax-id/update-customer", {
          name: trimmedName,
        }),
      });
    }
    if (taxIdProvided) {
      tasks.push({
        kind: "taxId",
        promise: Auth.fetchWithIdToken("/api/tax-id/create", {
          type: taxType,
          value: trimmedTaxValue,
        }),
      });
    }

    const outcomes = await Promise.all(
      tasks.map(async ({ kind, promise }) => {
        try {
          const result = await promise;
          return { kind, success: !!result.success, result };
        } catch (err) {
          return { kind, success: false, result: null };
        }
      })
    );

    const nameOutcome = outcomes.find((o) => o.kind === "name");
    const taxIdOutcome = outcomes.find((o) => o.kind === "taxId");

    if (nameOutcome?.success && nameOutcome.result?.customer) {
      setCustomer(nameOutcome.result.customer);
      setNameInput(nameOutcome.result.customer.name || "");
    }
    if (taxIdOutcome?.success) {
      setTaxValue("");
    }

    const successes = [];
    const failures = [];
    if (nameOutcome) {
      if (nameOutcome.success) {
        successes.push("Name updated");
      } else {
        failures.push(
          `Name update failed${
            nameOutcome.result?.error ? `: ${nameOutcome.result.error}` : ""
          }`
        );
      }
    }
    if (taxIdOutcome) {
      if (taxIdOutcome.success) {
        successes.push("Tax ID added");
      } else {
        failures.push(
          `Tax ID failed${
            taxIdOutcome.result?.error ? `: ${taxIdOutcome.result.error}` : ""
          }`
        );
      }
    }

    if (failures.length === 0) {
      setStatusMessage({ type: "success", text: `${successes.join(". ")}.` });
    } else if (successes.length === 0) {
      setStatusMessage({ type: "error", text: `${failures.join(". ")}.` });
    } else {
      setStatusMessage({
        type: "error",
        text: `${successes.join(". ")}. ${failures.join(". ")}.`,
      });
    }

    if (taxIdOutcome?.success) {
      await fetchInfo();
    }
    setSubmitting(false);
  };

  const handleRemove = async (taxIdId) => {
    setSubmitting(true);
    setStatusMessage(null);
    try {
      const result = await Auth.fetchWithIdToken("/api/tax-id/delete", {
        taxIdId,
      });
      if (result.success) {
        setStatusMessage({
          type: "success",
          text: "Tax ID removed successfully.",
        });
        await fetchInfo();
      } else {
        setStatusMessage({
          type: "error",
          text: result.error || "Failed to remove tax ID.",
        });
      }
    } catch (e) {
      setStatusMessage({
        type: "error",
        text: "Failed to remove tax ID. Please try again.",
      });
    }
    setSubmitting(false);
  };

  if (!user) {
    return (
      <PageWrapper>
        <BackButton href="/app">
          <BackArrow />
          Back to Projects
        </BackButton>
        <ContentWrapper>
          <SorbetCard>
            <Logo />
            <Title>Tax Information</Title>
            <Message>Please log in to manage your tax information.</Message>
            <SudoButton variant="primary" onClick={() => openLogin && openLogin()}>
              Log In
            </SudoButton>
          </SorbetCard>
        </ContentWrapper>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <BackButton href="/app">
        <BackArrow />
        Back to Projects
      </BackButton>
      <ContentWrapper>
        <SorbetCard>
          <Logo />
          <Title>Tax Information</Title>

          {loading && <Message>Loading tax information...</Message>}

          {!loading && !hasSubscription && (
            <InfoMessage>
              No billing account found. Please contact support if you believe
              this is an error.
            </InfoMessage>
          )}

          {!loading && hasSubscription && (
            <Form onSubmit={handleSubmit}>
              {customer && (
                <CustomerSection>
                  <FieldGroup>
                    <FieldLabel htmlFor="tax-id-name">Name</FieldLabel>
                    <Input
                      id="tax-id-name"
                      type="text"
                      aria-label="Customer name"
                      value={nameInput}
                      onChange={(e) => setNameInput(e.target.value)}
                      disabled={submitting}
                      placeholder="Your name"
                    />
                  </FieldGroup>
                  <FieldGroup>
                    <FieldLabel>Email</FieldLabel>
                    <EmailRow>
                      <EmailValue>{customer.email || "Not set"}</EmailValue>
                      <SwitchAccountLink
                        role="button"
                        tabIndex={0}
                        onClick={handleSwitchAccount}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleSwitchAccount();
                          }
                        }}
                      >
                        (Switch account)
                      </SwitchAccountLink>
                    </EmailRow>
                  </FieldGroup>
                </CustomerSection>
              )}

              {showTaxIdInputs && (
                <FormRow>
                  <FormGroup>
                    <MenuSelect
                      ref={taxTypeSelectRef}
                      id="tax-id-type"
                      variant="secondary"
                      placeholder="Select tax ID type"
                      value={taxType}
                      options={TAX_ID_TYPES}
                      optionAlignment="left"
                      menuType="button"
                      menuAlign="start"
                      className="!w-[var(--radix-select-trigger-width)]"
                      onChange={(value) => setTaxType(value)}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Input
                      id="tax-id-value"
                      type="text"
                      placeholder="Enter tax ID number"
                      value={taxValue}
                      onChange={(e) => setTaxValue(e.target.value)}
                    />
                  </FormGroup>
                </FormRow>
              )}

              {taxIds.length > 0 && (
                <TaxIdSection>
                  <TaxIdList>
                    {taxIds.map((tid) => (
                      <TaxIdItem key={tid.id}>
                        <TaxIdInfo>
                          <TaxIdType>
                            {getTaxTypeLabel(tid.type, TAX_ID_TYPES)}
                          </TaxIdType>
                          <TaxIdValue>{tid.value}</TaxIdValue>
                        </TaxIdInfo>
                        <SudoButton
                          variant="danger"
                          size="small"
                          type="button"
                          onClick={() => handleRemove(tid.id)}
                          disabled={submitting}
                        >
                          Remove
                        </SudoButton>
                      </TaxIdItem>
                    ))}
                  </TaxIdList>
                </TaxIdSection>
              )}

              {statusMessage?.type === "error" && (
                <ErrorMessage>{statusMessage.text}</ErrorMessage>
              )}
              {statusMessage?.type === "success" && (
                <SuccessMessage>{statusMessage.text}</SuccessMessage>
              )}

              <SudoButton
                variant="primary"
                type="submit"
                disabled={submitting || !canSubmit}
              >
                {ctaLabel}
              </SudoButton>
            </Form>
          )}
        </SorbetCard>
      </ContentWrapper>
    </PageWrapper>
  );
}

// Sorbet-themed wrapper around the shared `Card` primitive — adds the
// padding, internal layout, and max-width that Card itself does not provide.
const SorbetCard = styled(Card)`
  width: 100%;
  max-width: 600px;
  background: var(--menu-color, #fff);
  padding: 40px;
  text-align: left;

  @media (max-width: 640px) {
    padding: 24px 16px;
  }
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 400;
  font-family: "GT Super Text", serif;
  color: var(--grey-0, #383838);
  margin: 0 0 24px 0;
`;

const Message = styled.p`
  font-size: 15px;
  color: var(--grey-1, #686868);
  margin: 0 0 16px 0;
`;

const InfoMessage = styled.p`
  font-size: 15px;
  color: var(--grey-1, #686868);
  background: var(--menu-color, #f9f9fb);
  border: 1px solid var(--grey-5, #e0e0e0);
  border-radius: 10px;
  padding: 14px 18px;
  margin: 0 0 16px 0;
`;

const SuccessMessage = styled.p`
  font-size: 14px;
  color: var(--success-color);
  background: var(--success-color-hover, var(--menu-color));
  border-radius: 10px;
  padding: 10px 14px;
  margin: 0;
`;

const CustomerSection = styled.div`
  padding-bottom: 16px;
  border-bottom: 1px solid var(--grey-5, #eee);
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const EmailRow = styled.div`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px;
`;

const SwitchAccountLink = styled.span`
  font-size: 13px;
  color: var(--grey-1, #686868);
  cursor: pointer;
  text-decoration: underline;
  text-decoration-color: transparent;
  transition: color 0.15s, text-decoration-color 0.15s;

  &:hover {
    color: var(--grey-0, #383838);
    text-decoration-color: currentColor;
  }

  &:focus-visible {
    outline: 2px solid var(--primary-cta);
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

const TaxIdSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TaxIdList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TaxIdItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--menu-color, #f9f9fb);
  border: 1px solid var(--grey-5, #e0e0e0);
  border-radius: 10px;
  padding: 12px 14px;
`;

const TaxIdInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const TaxIdType = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: var(--primary-cta, #4808d1);
  text-transform: uppercase;
`;

const TaxIdValue = styled.span`
  font-size: 14px;
  color: var(--grey-0, #383838);
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FormRow = styled.div`
  display: flex;
  gap: 12px;

  @media (max-width: 640px) {
    flex-direction: column;
  }
`;

const FormGroup = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;
